import { Fragment, useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { GrRewardLogEntry } from '@/lib/api';
import { useItemDefsByIds } from '../hooks';

interface RewardLogTableProps {
  entries: GrRewardLogEntry[];
  /** Show the User column (resolved username, fallback to userId tail). */
  showUser?: boolean;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function RewardLogTable({ entries, showUser }: RewardLogTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Collect every item_def_id referenced in the expanded rewards so the
  // detail view can render names instead of `did_…` strings. The hook caches
  // per-id for 5 minutes, so re-renders are cheap.
  const itemDefIds = useMemo(() => {
    const ids = new Set<string>();
    for (const entry of entries) {
      for (const reward of entry.rewardsDelivered) {
        if (reward.itemDefId) ids.add(reward.itemDefId);
      }
    }
    return Array.from(ids);
  }, [entries]);
  const { byId: itemNameById } = useItemDefsByIds(itemDefIds);

  const toggleRow = (logId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(logId)) {
        next.delete(logId);
      } else {
        next.add(logId);
      }
      return next;
    });
  };

  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">No reward log entries found.</p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-10" />
          {showUser && <TableHead>User</TableHead>}
          <TableHead>Game</TableHead>
          <TableHead>Game Event</TableHead>
          <TableHead>Score</TableHead>
          <TableHead>Rewards</TableHead>
          <TableHead>Date</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.map((entry) => {
          const isExpanded = expandedRows.has(entry.logId);
          const rewardCount = entry.rewardsDelivered.length;

          return (
            <Fragment key={entry.logId}>
              <TableRow>
                <TableCell>
                  {rewardCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => toggleRow(entry.logId)}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </TableCell>
                {showUser && (
                  <TableCell className="text-sm">
                    {entry.username ?? <span className="text-muted-foreground">unknown</span>}
                  </TableCell>
                )}
                <TableCell className="text-sm">
                  {entry.displayName ?? entry.gameName ?? (
                    <span
                      className="text-muted-foreground font-mono text-xs"
                      title={entry.gameConfigId}
                    >
                      {entry.gameConfigId}
                    </span>
                  )}
                </TableCell>
                <TableCell>{entry.gameEvent}</TableCell>
                <TableCell>{entry.gameScore}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{rewardCount} delivered</Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDate(entry.createdAt)}
                </TableCell>
              </TableRow>
              {isExpanded && rewardCount > 0 && (
                <TableRow key={`${entry.logId}-detail`}>
                  <TableCell colSpan={showUser ? 7 : 6} className="bg-muted/30 px-8 py-3">
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Reward Details
                      </p>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Type</TableHead>
                            <TableHead>Item</TableHead>
                            <TableHead>Raw Amount</TableHead>
                            <TableHead>Multiplier</TableHead>
                            <TableHead>Final Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {entry.rewardsDelivered.map((reward, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{reward.rewardType}</TableCell>
                              <TableCell>
                                {itemNameById[reward.itemDefId] ?? (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </TableCell>
                              <TableCell>{reward.rawAmount}</TableCell>
                              <TableCell>{reward.multiplier}x</TableCell>
                              <TableCell className="font-semibold">{reward.finalAmount}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
